/* eslint-disable @buildwithflux/no-simple-lodash */
import { pcbLayoutRootNodeId, PcbNodeTypes, PrimitiveType, } from "@buildwithflux/models";
import { SubscriptionManager } from "@buildwithflux/repositories";
import { forEach, groupBy, has, isEqualWith, get as lget, set as lset } from "lodash";
import { create } from "mutative";
import RBush from "rbush";
import { Box2, Box3, Vector2, Vector3 } from "three";
import { box2 } from "../../../common/geometry/box2";
import { geom2 } from "../../../common/geometry/geom2";
import { mat3 } from "../../../common/geometry/mat3";
import { mat4 } from "../../../common/geometry/mat4";
import { createPrimitivesForNode } from "../../layout_engine/primitives_generation";
import { TraceCorners } from "../../libs/traceCorners";
import { getBoundsForPad } from "../helpers/aabb";
import { approxAABoundingBox, mergeApproxBoundingBoxes } from "../math";
import { pointInShape, Query } from "../queries";
import { PcbPrimitiveStoreOperation, SupportedNodeTypes, } from "../types";
import { NodeToObject3DMap } from "./deprecatedMapping";
import { guessPrimaryPrimitiveForNodeTransform, getWorldPositionFromContainer, iterateBatchedAndBatchedDescendants, } from "./utils";
var UpsertAction;
(function (UpsertAction) {
    UpsertAction[UpsertAction["Add"] = 0] = "Add";
    UpsertAction[UpsertAction["Update"] = 1] = "Update";
})(UpsertAction || (UpsertAction = {}));
/** An efficient store for PCB shapes. Shapes are referred to as primitives */
export class PcbPrimitiveStore {
    subscriptionManager = new SubscriptionManager();
    // Used to notify the rendering system when a transient change is applied to a node during interactions. Transient changes
    // include interactions like mouse-driven drag, or keyboard-driven nudging, where immediate updates are expected
    interactionSubscription = new SubscriptionManager();
    nodeUidList = [];
    nodePrimitivesMap = new Map();
    containerMap = new Map(); // a map of layout id <> primitives
    interactionPositions = new Map();
    // TODO:
    // We want to remove the old ShapesMap for fills and use the ShapesStrategy instead.
    // For now we just did the first step of merging the two stores together by copy-pasting,
    // but we actually want to remove this and use the nodeShapesMap instead
    fillShapesMap = {};
    // ================================================================
    // Interface functions for the PcbBakedGoodsManager
    // ================================================================
    // TODO: refactor this
    setFills(fills) {
        const newFillShapesMap = create(this.fillShapesMap, (draftState) => {
            // First look for and remove any primitives that are currently in draftState, that are not present
            // in incoming state. It's important to remove primitives that are no longer present in incoming state,
            // to ensure the PCB canvas remains visually in sync with the output of virtual fill generation
            forEach(draftState, (layout, netUid) => {
                forEach(layout, (layers, layoutUid) => {
                    forEach(layers, (_, layerId) => {
                        // Fill geometry on net / layout / layer still exists in incoming state meaining no removal is needed
                        if (has(fills, [netUid, layoutUid, layerId])) {
                            return;
                        }
                        // If net not present in draft state, then there's nothing to do here
                        const net = draftState[netUid];
                        if (!net) {
                            return;
                        }
                        // Look for and delete geometry on net / layout / layer in current state
                        const layout = net[layoutUid];
                        if (layout) {
                            delete layout[layerId];
                        }
                        // We might've just mutated the layout. Check if it's now empty and if so,
                        // delete it from the net
                        if (isEqualWith(layout, {})) {
                            delete net[layoutUid];
                        }
                        // Finally, we might've just mutated the net. Check if it's now empty and if so,
                        // delete it from the draft state
                        if (isEqualWith(net, {})) {
                            delete draftState[netUid];
                        }
                    });
                });
            });
            // Next, we iterate the incoming fill state, looking for newly added or updated polygons. We
            // upsert the draftState wherever these additions or changes are found
            forEach(fills, (layouts, netUid) => {
                forEach(layouts, (layers, layoutUid) => {
                    forEach(layers, (newPolygons, layerId) => {
                        const oldPolygons = lget(this.fillShapesMap, [netUid, layoutUid, layerId], []);
                        if (geom2.arePolygonsDiff(newPolygons, oldPolygons)) {
                            lset(draftState, [netUid, layoutUid, layerId], newPolygons);
                        }
                    });
                });
            });
        });
        this.fillShapesMap = newFillShapesMap;
        this.subscriptionManager.notify(PcbPrimitiveStoreOperation.UpdateFills, {
            operation: PcbPrimitiveStoreOperation.UpdateFills,
            primitivesStore: this,
            updatedNodeUids: [],
        });
    }
    applyChange(documentChange, newBakedNodes, tracingGraph) {
        this.interactionPositions.clear();
        const { pcbLayoutNodes } = documentChange.payload;
        if (!pcbLayoutNodes) {
            return;
        }
        const hasUids = (uids) => {
            return uids && uids.length > 0 ? uids : undefined;
        };
        const add = hasUids(pcbLayoutNodes.add);
        const remove = hasUids(pcbLayoutNodes.remove);
        const replace = hasUids(pcbLayoutNodes.replace);
        const set = hasUids(pcbLayoutNodes.set);
        if (!add && !remove && !replace && !set) {
            return;
        }
        // Perform the requested operations first
        if (remove) {
            this.removeNodesOnly(remove, newBakedNodes);
        }
        if (add) {
            this.addNodesOnly(add, newBakedNodes, tracingGraph);
        }
        if (replace) {
            this.updateNodesOnly(replace, newBakedNodes, tracingGraph);
        }
        if (set) {
            this.updateNodesOnly(set, newBakedNodes, tracingGraph);
        }
        // Rebuild the R-Trees once, after all operations are done, rather than per operation
        this.rebuildRTrees();
        // Preserve execution order, by notifying subscribers after all operations are done
        if (remove) {
            this.subscriptionManager.notify(PcbPrimitiveStoreOperation.Remove, {
                operation: PcbPrimitiveStoreOperation.Remove,
                primitivesStore: this,
                updatedNodeUids: remove,
            });
        }
        if (add) {
            this.subscriptionManager.notify(PcbPrimitiveStoreOperation.Add, {
                operation: PcbPrimitiveStoreOperation.Add,
                primitivesStore: this,
                // TODO: consider in event of nodeUid from nodeUids not corresponding to a node 👉 nodesToUpdate.map((node) => node.uid),
                updatedNodeUids: add,
                updateAll: false,
            });
        }
        if (replace) {
            this.subscriptionManager.notify(PcbPrimitiveStoreOperation.Update, {
                operation: PcbPrimitiveStoreOperation.Update,
                primitivesStore: this,
                // TODO: consider in event of nodeUid from nodeUids not corresponding to a node 👉 nodesToUpdate.map((node) => node.uid),
                updatedNodeUids: replace,
                updateAll: true,
            });
        }
        if (set) {
            this.subscriptionManager.notify(PcbPrimitiveStoreOperation.Update, {
                operation: PcbPrimitiveStoreOperation.Update,
                primitivesStore: this,
                // TODO: consider in event of nodeUid from nodeUids not corresponding to a node 👉 nodesToUpdate.map((node) => node.uid),
                updatedNodeUids: set,
                updateAll: true,
            });
        }
    }
    loadLayoutNodes(newBakedNodes, tracingGraph) {
        this.clear();
        const nodeUids = Object.keys(newBakedNodes);
        this.addNodesOnly(nodeUids, newBakedNodes, tracingGraph);
        this.rebuildRTrees();
        this.subscriptionManager.notify(PcbPrimitiveStoreOperation.Add, {
            operation: PcbPrimitiveStoreOperation.Add,
            primitivesStore: this,
            updatedNodeUids: nodeUids, // TODO: consider in event of nodeUid from nodeUids not corresponding to a node 👉 nodesToUpdate.map((node) => node.uid),
            updateAll: false,
        });
    }
    /** This function is called only by the PcbBakedGoodsManager to update the store */
    clear() {
        this.nodePrimitivesMap.clear();
        this.containerMap.clear();
        this.nodeUidList.length = 0; // Setting zero is faster & simpler compared to splice
        this.interactionPositions.clear();
        // TODO: refactor this
        this.fillShapesMap = {};
    }
    // ================================================================
    // Public interface functions for consumers
    // ================================================================
    /**
     * Get a map with all the PCB layout nodes and their associated primitives.
     * This map is NOT immutable: it will automatically update and it will be referentially stable.
     */
    getAllNodePrimitivesMap() {
        return this.nodePrimitivesMap;
    }
    getAllNodePrimitives() {
        const records = {};
        for (const uid of this.nodePrimitivesMap.keys()) {
            const quadTreeNode = this.nodePrimitivesMap.get(uid);
            if (quadTreeNode) {
                records[uid] = quadTreeNode;
            }
        }
        return records;
    }
    /**
     * Get a map with all the nodes with layout/footprint type and their associated transform matrices.
     * This map is NOT immutable: it will automatically update and it will be referentially stable.
     */
    getAllContainersMap() {
        return this.containerMap;
    }
    getAllNodeUidsWithShapes() {
        // TODO: rename
        return this.nodeUidList;
    }
    // TODO: refactor this
    getFillShapesMap() {
        return this.fillShapesMap;
    }
    /**
     * Get local coordinates of vector relative to node
     */
    getNodePositionLocal(nodeUid, vector = { x: 0, y: 0 }) {
        // For now, we're using the old mapping to get the object3D as a safe guard against regression
        // Note: this is currently very important, to support nodes like nets and containers that aren't
        // included in `SupportedNodeTypes`
        const object3D = NodeToObject3DMap.get(nodeUid);
        if (object3D) {
            object3D.updateWorldMatrix(true, true);
            const local = object3D.worldToLocal(new Vector3(vector.x, vector.y, 0));
            return {
                x: local.x,
                y: local.y,
            };
        }
        // The primitive store currently doesn't support root node, and nor does NodeToObject3DMap:
        if (nodeUid === pcbLayoutRootNodeId) {
            return { x: 0, y: 0 };
        }
        // If we're here, it's likely because batched pads are enabled - we attempt to derive the local
        // position from the primitive store instead
        const entry = this.nodePrimitivesMap.get(nodeUid);
        const matrix = guessPrimaryPrimitiveForNodeTransform(entry)?.transformInverse;
        if (matrix) {
            const local = new Vector2(vector.x, vector.y).applyMatrix3(mat3.toThreeMatrix3(matrix));
            return {
                x: local.x,
                y: local.y,
            };
        }
        return null;
    }
    /**
     * Get world coordinates of vector relative to node
     */
    getNodePositionWorld(nodeUid) {
        if (this.interactionPositions.has(nodeUid)) {
            const position = this.interactionPositions.get(nodeUid);
            if (position) {
                return {
                    x: position.x,
                    y: position.y,
                };
            }
        }
        // For now, we're using the old mapping to get the object3D as a safe guard against regression
        // Note: this is currently very important, to support nodes like nets and containers that aren't
        // included in `SupportedNodeTypes`
        const object3D = NodeToObject3DMap.get(nodeUid);
        if (object3D) {
            object3D.updateWorldMatrix(true, true);
            const world = object3D.localToWorld(new Vector3(0, 0, 0));
            return {
                x: world.x,
                y: world.y,
            };
        }
        // The primitive store currently doesn't support root node, and nor does NodeToObject3DMap:
        if (nodeUid === pcbLayoutRootNodeId) {
            return { x: 0, y: 0 };
        }
        // If we're here, it's likely because batched pads are enabled - we attempt to derive the local
        // position from the primitive store instead
        const entry = this.nodePrimitivesMap.get(nodeUid);
        const container = entry ? this.containerMap.get(entry.containerId) : null;
        const matrix = guessPrimaryPrimitiveForNodeTransform(entry)?.transform;
        if (!matrix || !container) {
            return null;
        }
        const positionLayer = new Vector2(0, 0).applyMatrix3(mat3.toThreeMatrix3(matrix));
        const positionWorld = getWorldPositionFromContainer(positionLayer, container);
        return {
            x: positionWorld.x,
            y: positionWorld.y,
        };
    }
    /**
     * Get the AABB (axis-aligned bounding box) of a node in container space.
     */
    getNodeAABBContainerSpace(nodeUid, options) {
        const boxFromObject3DMap = this.getBoxFromObject3DMapIfRequired(nodeUid, options);
        if (boxFromObject3DMap) {
            return boxFromObject3DMap;
        }
        const node = this.nodePrimitivesMap.get(nodeUid);
        if (!node) {
            return null;
        }
        const box = new Box2().setFromPoints([new Vector2(node.minX, node.minY), new Vector2(node.maxX, node.maxY)]);
        // Return a hard-null value for [+Infinity, -Infinity] boxes, as above.
        return box2.isValid(box) ? box : null;
    }
    /**
     * Get the AABB (axis-aligned bounding box) of a node in document space.
     */
    getNodeAABBDocumentSpace(nodeUid, options) {
        const boxFromObject3DMap = this.getBoxFromObject3DMapIfRequired(nodeUid, options);
        if (boxFromObject3DMap) {
            return boxFromObject3DMap;
        }
        const node = this.nodePrimitivesMap.get(nodeUid);
        if (!node) {
            return null;
        }
        const containerTransform = mat4.toThreeMatrix4(this.containerMap.get(node.containerId)?.transform ?? [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
        const box3 = new Box3().setFromPoints([
            new Vector3(node.minX, node.minY, 0),
            new Vector3(node.maxX, node.maxY, 0),
        ]);
        box3.applyMatrix4(containerTransform);
        const box = new Box2(new Vector2(box3.min.x, box3.min.y), new Vector2(box3.max.x, box3.max.y));
        // Return a hard-null value for [+Infinity, -Infinity] boxes, as above.
        return box2.isValid(box) ? box : null;
    }
    subscribe(operation, callback) {
        return this.subscriptionManager.addSubscription(operation, callback);
    }
    /**
     * Get the AABB (axis-aligned bounding box) of a node in document space, adjusted for interaction changes.
     */
    interactionAABB(selectedNode, offset) {
        switch (selectedNode?.type) {
            case PcbNodeTypes.pad: {
                const bounds = getBoundsForPad(selectedNode);
                return box2.applyMatrix4(bounds, offset); // Apply the offset to the bounds
            }
            // TODO: add support for other node types here
            default: {
                return null;
            }
        }
    }
    /**
     * Notify the renderers that some node has received a transient interaction, triggering a presentation update
     * Transient interactions are things like user drag, etc, where visible updates are required between/prior to baking
     */
    interactionNotify(payload, params) {
        // For now, we're using the old mapping to get the object3D as a safe guard against regression
        const object3D = NodeToObject3DMap.get(payload.nodeUid);
        if (object3D) {
            object3D.position.x = payload.x;
            object3D.position.y = payload.y;
            // TODO: ??
            // withAdditionalAPI(pcbNodeMesh)?.invalidate();
            params?.afterNodePositionUpdated?.(object3D);
        }
        this.interactionPositions.set(payload.nodeUid, { x: payload.x, y: payload.y });
        this.interactionSubscription.notify("interaction", payload);
    }
    interactionSubscribe(callback) {
        return this.interactionSubscription.addSubscription("interaction", callback);
    }
    /**
     * Work-in-progress idea - provides access to iteration over nodes and descendants known to be affected
     * by drag or move interaction. Since this requires a nodes map, the method might ultimately be moved to
     * elsewhere but for now it's being put here to collocate with the interaction subscription stuff
     *
     * Note also, it currently only works with pads - since pads are the only node type that supports batching
     */
    interactionIterateNodesAndDescendants(nodes, uids, flags) {
        return iterateBatchedAndBatchedDescendants(nodes, uids, flags);
    }
    /**
     * Check if a point is contained in the given primitive.
     */
    isPointContained(primitive, point) {
        // TODO: this will be fixed in FLUX-6407
        return pointInShape(primitive, point);
    }
    /**
     * Query the store for all the RBush nodes with one or more primitives that overlaps the specified bounding box.
     *
     * Currently only supports accurate bound box queries with Circle and Oblong primitives.
     */
    *queryByBoundingBoxDocumentSpace(documentSpaceQueryBox) {
        const box3 = new Box3();
        for (const [, container] of this.getAllContainersMap()) {
            box3.min.set(documentSpaceQueryBox.min.x, documentSpaceQueryBox.min.y, 0);
            box3.max.set(documentSpaceQueryBox.max.x, documentSpaceQueryBox.max.y, 0);
            box3.applyMatrix4(mat4.toThreeMatrix4(container.transformInverse));
            const isBoxOverlap = Query.BoundBox(box3.min, box3.max).Overlaps;
            for (const quadNode of container.quadTree.search(box2.toRBush(box3))) {
                let hit = null;
                for (const primitive of quadNode.primitives) {
                    switch (primitive.type) {
                        case PrimitiveType.Circle: {
                            hit = isBoxOverlap.Circle(primitive.radius, primitive.transform);
                            break;
                        }
                        case PrimitiveType.Oblong: {
                            hit = isBoxOverlap.Oblong(primitive.width, primitive.height, primitive.transform);
                            break;
                        }
                    }
                    if (hit) {
                        break;
                    }
                }
                if (hit === null || hit) {
                    yield quadNode;
                }
            }
        }
    }
    /**
     * Encapsulates the logic of getting a bounding box from the Object3D map for reuse between different methods requiring
     * this same options processing logic.
     *
     * This should be considered a temporary solution, as the Object3D map is being phased out
     */
    getBoxFromObject3DMapIfRequired(nodeUid, options) {
        if (!options?.tempForceUsePrimitiveStore) {
            // For now, we're using the old mapping to get the object3D as a safe guard against regression
            // Note: this is currently very important, to support nodes like nets and containers that aren't
            // included in `SupportedNodeTypes`
            let object3D = NodeToObject3DMap.get(nodeUid);
            if (nodeUid === pcbLayoutRootNodeId) {
                // The primitive store currently doesn't support root node, and nor does NodeToObject3DMap.
                // TODO this is a hack. We don't have an object in NodeToObject3DMap for the root node, but
                // we do know that we use object.name to store node type. We can quickly walk up the threejs
                // hierarachy from any node to find the object corresponding to the root node.
                let candidate = [...NodeToObject3DMap.values()][0];
                while (candidate && candidate.name !== PcbNodeTypes.root) {
                    candidate = candidate.parent ?? undefined;
                }
                object3D = candidate;
            }
            if (object3D) {
                const box = options?.interceptBoxCreation
                    ? options.interceptBoxCreation(object3D)
                    : new Box3().setFromObject(object3D);
                // Return a hard-null value for [+Infinity, -Infinity] boxes, to avoid the caller trying to
                // do something weird with them. This occurs for eg with empty nodes, such as a net with no
                // traces.
                return box2.isValid(box)
                    ? new Box2(new Vector2(box.min.x, box.min.y), new Vector2(box.max.x, box.max.y))
                    : null;
            }
        }
    }
    updateNodesOnly(nodeUids, allNodes, tracingGraph) {
        this.upsertNodesOnly(nodeUids, allNodes, tracingGraph, UpsertAction.Update);
    }
    addNodesOnly(nodeUids, allNodes, tracingGraph) {
        this.upsertNodesOnly(nodeUids, allNodes, tracingGraph, UpsertAction.Add);
    }
    removeNodesOnly(nodeUids, _allNodes) {
        for (const nodeUid of nodeUids) {
            const index = this.nodeUidList.indexOf(nodeUid);
            if (index !== -1) {
                this.nodeUidList.splice(this.nodeUidList.indexOf(nodeUid), 1);
            }
            this.nodePrimitivesMap.delete(nodeUid);
            this.containerMap.delete(nodeUid);
        }
    }
    upsertNodesOnly(nodeUids, allNodes, tracingGraph, action) {
        const accumulateConnectedRoutes = (routeSegment) => {
            // Find and add all route segments, connected to the current route segment, to the nodes array
            for (const adjacentRouteSegment of TraceCorners.connectedToCornerTraceGenerator(routeSegment.uid, allNodes, tracingGraph.getGraph())) {
                nodesToUpdate.push(adjacentRouteSegment);
            }
        };
        // Collects all nodes to be updated, for uid's that are to be added/updated. Prefetching nodes by uid simplifies later
        // logic in this function. It also provides an opportunity to collect any route segment nodes, that are connected to
        // route segments in the specified uids. Doing this unifies the logic for updating primitives-by-node, in the later logic
        // of this function; non-route segments, route segments, and connected route segments (not present in the nodeUids array)
        // can all receive primitive updates with the same logic.
        const nodesToUpdate = [];
        for (const nodeUid of nodeUids) {
            const node = allNodes[nodeUid];
            if (!node) {
                continue;
            }
            if (node.type === PcbNodeTypes.routeSegment) {
                // Route segments are unique in that, they may be connected by a curved arc, to another route segment. We
                // therefore need to find and also collect those route segments, to have them included for primitive update
                // (so that DRC, rendering frontend, etc are notified and kept in sync)
                accumulateConnectedRoutes(node);
            }
            nodesToUpdate.push(node);
        }
        for (const node of nodesToUpdate) {
            // Question: should this be done upfront? Perhaps only do this, after early exits, to avoid possibility of redundant
            // container (or container being added before it's needed)
            if (node.parentUid === pcbLayoutRootNodeId &&
                (node.type === PcbNodeTypes.layout || node.type === PcbNodeTypes.footprint)) {
                const transform = node.bakedRules.transformRelativeToDocument;
                const transformInverse = mat4.invert(node.bakedRules.transformRelativeToDocument);
                // Creates a new "layer container" for each layout or footprint, which will provide a 3D transform context
                this.containerMap.set(node.uid, {
                    transform,
                    transformInverse,
                    quadTree: new RBush(),
                });
            }
            if (!SupportedNodeTypes.includes(node.type)) {
                continue;
            }
            if (action !== UpsertAction.Update) {
                this.nodeUidList.push(node.uid);
            }
            const layoutOrFootprintNode = allNodes[node.bakedRules.rootFootprintOrLayoutUid ?? ""];
            if (layoutOrFootprintNode?.type !== PcbNodeTypes.layout &&
                layoutOrFootprintNode?.type !== PcbNodeTypes.footprint) {
                // If we have no root layout/footprint we have no stackup, so we can't emit primitives
                // TODO: this should not be possible but we could still put those primitives in an hardcoded layer
                continue;
            }
            // TODO: this can be optimized by passing the stackup as baked value
            const stackup = Object.values(layoutOrFootprintNode.bakedRules.stackup ?? {}).sort((a, b) => a.order - b.order);
            const primitives = createPrimitivesForNode(node, allNodes, stackup, tracingGraph);
            const treeNode = {
                nodeUid: node.uid,
                containerId: layoutOrFootprintNode.uid,
                primitives,
                ...mergeApproxBoundingBoxes(primitives.map((primitive) => approxAABoundingBox(primitive))),
            };
            this.nodePrimitivesMap.set(node.uid, treeNode);
        }
    }
    // TODO: for now we take the safe route and rebuild the RTree every time as it's still cheap, but this can be optimized
    rebuildRTrees() {
        const nodesWithBB = Array.from(this.nodePrimitivesMap.values());
        const partitionedNodes = groupBy(nodesWithBB, (a) => a.containerId);
        for (const [containerId, container] of this.containerMap) {
            container.quadTree.clear();
            container.quadTree.load(partitionedNodes[containerId] ?? []);
        }
    }
}
//# sourceMappingURL=PcbPrimitiveStore.js.map